import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { usePwaInstall } from '@/hooks/usePwaInstall';
import { Check, Download, LoaderCircle, Share, Smartphone } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { toast } from 'sonner';

function isIosDevice() {
  if (typeof navigator === 'undefined') return false;

  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

function Benefit({ children }: { children: ReactNode }) {
  return (
    <li className="flex items-center gap-2 text-sm">
      <span className="bg-primary/10 text-primary flex h-6 w-6 shrink-0 items-center justify-center rounded-full">
        <Check className="h-3.5 w-3.5" aria-hidden="true" />
      </span>
      {children}
    </li>
  );
}

function ManualInstallInstructions() {
  const ios = isIosDevice();

  return (
    <div className="bg-muted/60 rounded-xl border p-3">
      <p className="mb-2 text-sm font-semibold">{ios ? 'No iPhone ou iPad' : 'Pelo menu do navegador'}</p>
      <ol className="text-muted-foreground space-y-2 text-sm">
        <li>
          <strong className="text-foreground">1.</strong>{' '}
          {ios ? (
            <>
              Toque em Compartilhar <Share className="inline h-4 w-4 align-text-bottom" aria-hidden="true" />
            </>
          ) : (
            <>
              Abra o menu do navegador <span aria-hidden="true">⋮</span>
            </>
          )}
        </li>
        <li>
          <strong className="text-foreground">2.</strong> Escolha{' '}
          <span className="text-foreground font-medium">{ios ? 'Adicionar à Tela de Início' : 'Instalar app'}</span>
        </li>
        <li>
          <strong className="text-foreground">3.</strong> Confirme em{' '}
          <span className="text-foreground font-medium">{ios ? 'Adicionar' : 'Instalar'}</span>
        </li>
      </ol>
      {ios ? (
        <p className="text-muted-foreground mt-2 text-xs">Abra esta página no Safari para ver essa opção.</p>
      ) : null}
    </div>
  );
}

export function InstallAppButton() {
  const { canInstall, install, isInstalled } = usePwaInstall();
  const [open, setOpen] = useState(false);
  const [installing, setInstalling] = useState(false);

  if (isInstalled) return null;

  const handleInstall = async () => {
    setInstalling(true);

    try {
      const installed = await install();

      if (installed) {
        setOpen(false);
        toast.success('Vira Tempo instalado com sucesso');
      } else {
        toast.info('Instalação cancelada', { description: 'Você pode tentar novamente quando quiser.' });
      }
    } catch {
      toast.error('Não foi possível instalar o app', { description: 'Tente pelo menu do navegador.' });
    } finally {
      setInstalling(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" className="h-10 gap-2 px-2.5 sm:px-3" aria-label="Instalar app">
          <Download className="h-4 w-4" aria-hidden="true" />
          <span className="hidden lg:inline">Instalar app</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={8} className="w-[min(22rem,calc(100vw-1rem))] space-y-4 rounded-2xl p-4">
        <div className="flex items-start gap-3">
          <span className="bg-primary/10 text-primary flex h-11 w-11 shrink-0 items-center justify-center rounded-xl">
            <Smartphone className="h-5 w-5" aria-hidden="true" />
          </span>
          <div>
            <h2 className="font-semibold">Instale o Vira Tempo</h2>
            <p className="text-muted-foreground mt-0.5 text-sm">Acesse seus agendamentos direto da tela inicial.</p>
          </div>
        </div>

        <ul className="space-y-2">
          <Benefit>Acesso mais rápido, sem abrir o navegador</Benefit>
          <Benefit>Experiência em tela cheia</Benefit>
          <Benefit>Instalação grátis e leve</Benefit>
        </ul>

        {canInstall ? (
          <Button className="h-11 w-full rounded-xl" disabled={installing} onClick={() => void handleInstall()}>
            {installing ? (
              <LoaderCircle className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Download className="mr-2 h-4 w-4" aria-hidden="true" />
            )}
            {installing ? 'Abrindo instalação…' : 'Instalar agora'}
          </Button>
        ) : (
          <ManualInstallInstructions />
        )}
      </PopoverContent>
    </Popover>
  );
}
